// The app's one language picker.
//
// Five screens used to each carry their own copy of the option list — three
// different object shapes, two different fallbacks for "current language"
// (Welcome defaulted to index 1, Landing to index 0), and NL spelled "Dutch" on
// the onboarding screens and "Nederlands" everywhere else. The data now comes
// from LANGUAGES (src/i18n/locale.ts); this component owns the rendering.
//
// The three variants exist because the screens genuinely look different, not
// because the list differs:
//   • "flag-menu"    — Welcome, Landing (dropdown with flags + active dot)
//   • "compact-menu" — OnboardingIdentity, OnboardingAvatar (small text menu)
//   • "grid"         — Settings (auto-fill grid, N languages)
//
// "grid" replaced the old "row" variant (flex-1 buttons in ONE non-wrapping
// line). That layout divided the row equally among however many languages
// existed, so at six of them Polski and Български were crushed to unreadable,
// untappable slivers — two shipped languages nobody could select. The grid
// computes its column count from the available width with a minimum readable
// cell, so a seventh or eighth language adds a row instead of shrinking anyone.

// ── ESTADO PENDENTE (code-splitting dos locales) ─────────────────────────────
//
// Escolher um idioma deixou de ser instantâneo. O dicionário virou um chunk
// próprio, baixado sob demanda: em 4G são 100-300 ms entre o toque e a tela
// mudar, e a troca só comita DEPOIS que o chunk chega (o contrário pintaria a
// tela inteira de chave crua durante o download).
//
// Espera sem sinal não parece lenta, parece BOTÃO QUEBRADO. O usuário toca de
// novo, às vezes noutro idioma, e vira duas trocas em voo. Por isso as três
// variantes mostram: qual idioma está carregando, que o grupo está ocupado
// (aria-busy + disabled) e, se falhar, o que falhou e que dá para tentar de
// novo.
//
// A copy do erro NÃO inventa chave: usa `common.error` + `common.retry`, que já
// existem nos nove idiomas, ao lado do `nativeLabel` do idioma que falhou (esse
// nunca é traduzido). Chave nova aqui exigiria passar pelo pipeline de tradução
// com glossário travado, e escrever à mão nove strings fora dele é o que
// destrói a consistência em silêncio (CLAUDE.md §4.6).
//
// O `onSelect` do chamador (fechar menu, analytics, marcar escolha explícita)
// passou a disparar SÓ NO SUCESSO. Disparar no clique diria que a escolha valeu
// num instante em que ela ainda podia falhar — e fecharia o menu justamente
// onde o erro precisa aparecer.

import { useRef } from "react";
import { Loader2 } from "lucide-react";
import { LANGUAGES, type Lang } from "@/i18n/locale";
import { useLanguage } from "@/i18n/LanguageProvider";

export type LanguageSelectorVariant = "flag-menu" | "compact-menu" | "grid";

interface LanguageSelectorProps {
  variant: LanguageSelectorVariant;
  /**
   * Extra work after the language is set — closing the popover, analytics,
   * persisting the "user chose explicitly" flag. The language switch itself is
   * always handled here so no call site can forget it.
   */
  onSelect?: (code: Lang) => void;
  /** Accessible name for the grid variant's radiogroup. */
  ariaLabel?: string;
}

const LanguageSelector = ({ variant, onSelect, ariaLabel }: LanguageSelectorProps) => {
  const { lang, setLang, pendingLang, failedLang, t } = useLanguage();
  const radioRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const ocupado = pendingLang !== null;
  const falhou = failedLang ? LANGUAGES.find((d) => d.code === failedLang) : null;

  const choose = (code: Lang) => {
    // Sem guarda de "já está ocupado" aqui, de propósito. Quem barra o segundo
    // toque é o `disabled={ocupado}` nos botões das três variantes — evento de
    // clique não dispara em botão desabilitado, teclado incluído.
    //
    // A guarda existiu por um commit e foi removida por MUTAÇÃO: apagá-la não
    // derrubou teste nenhum, o que é a definição de ramo que não carrega nada.
    // Mantê-la faria duas coisas ruins ao mesmo tempo — sugerir ao próximo
    // leitor que o `disabled` sozinho não basta, e esconder uma eventual
    // regressão NELE atrás de uma segunda rede que ninguém testa.
    //
    // Quem protege a corrida de verdade (duas trocas em voo por outro caminho
    // que não o seletor) é o id de pedido no LanguageProvider, esse sim com
    // teste próprio.
    void setLang(code).then((trocou) => {
      if (trocou) onSelect?.(code);
    });
  };

  /**
   * A linha de erro. `aria-live="assertive"` e não "polite": a pessoa acabou de
   * tocar e está esperando — a leitura tem de interromper, não entrar na fila.
   * Fica no fim de cada variante, dentro do mesmo container do menu/grade, para
   * o menu NÃO fechar e levar o erro junto.
   */
  const linhaDeErro = falhou ? (
    <p
      role="alert"
      aria-live="assertive"
      data-testid="language-error"
      className="px-4 py-2 text-[12px] font-display leading-snug text-destructive"
    >
      {falhou.nativeLabel} · {t("common.error")} — {t("common.retry")}
    </p>
  ) : null;

  // Roving tabindex over the grid: arrows move focus, Enter/Space selects.
  // Selection deliberately does NOT follow focus — choosing a language
  // re-renders the whole app in it, far too heavy a side effect for a
  // browsing keystroke.
  const moveFocus = (e: React.KeyboardEvent, current: number) => {
    const last = LANGUAGES.length - 1;
    let target: number;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      target = current === last ? 0 : current + 1;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      target = current === 0 ? last : current - 1;
    } else if (e.key === "Home") {
      target = 0;
    } else if (e.key === "End") {
      target = last;
    } else {
      return;
    }
    e.preventDefault();
    radioRefs.current[target]?.focus();
  };

  if (variant === "grid") {
    return (
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(7rem,1fr))]"
      >
        {LANGUAGES.map((option, index) => {
          const active = lang === option.code;
          const carregando = pendingLang === option.code;
          return (
            <button
              key={option.code}
              type="button"
              role="radio"
              aria-checked={active}
              aria-busy={carregando}
              disabled={ocupado}
              tabIndex={active ? 0 : -1}
              ref={(el) => { radioRefs.current[index] = el; }}
              onClick={() => choose(option.code)}
              onKeyDown={(e) => moveFocus(e, index)}
              className={`min-h-[44px] py-3 px-2 rounded-xl flex items-center justify-center gap-1.5 transition-all duration-250 ${
                active
                  ? "bg-primary/10 border border-primary/25 shadow-[0_0_12px_hsl(187_72%_50%/0.08)]"
                  : "bg-card/30 border border-border/12 hover:bg-card/45 hover:border-border/20"
              } ${
                // Só os OUTROS esmaecem: o que está carregando fica legível,
                // porque é justamente nele que o usuário está olhando.
                ocupado && !carregando ? "opacity-40" : ""
              } disabled:cursor-default`}
            >
              {carregando ? (
                <Loader2
                  className="w-[14px] h-[14px] animate-spin text-primary"
                  aria-hidden="true"
                  data-testid={`language-spinner-${option.code}`}
                />
              ) : (
                <span className="text-[14px] leading-none" aria-hidden="true">
                  {option.flag}
                </span>
              )}
              <p
                className={`text-[12px] font-display font-semibold leading-tight ${
                  active || carregando ? "text-primary" : "text-foreground/60"
                }`}
              >
                {option.nativeLabel}
              </p>
            </button>
          );
        })}
        {/* col-span-full: a linha de erro ocupa a largura da grade inteira,
            em vez de virar mais uma célula de idioma. */}
        {falhou && <div className="col-span-full">{linhaDeErro}</div>}
      </div>
    );
  }

  if (variant === "compact-menu") {
    return (
      <>
        {LANGUAGES.map((option) => {
          const carregando = pendingLang === option.code;
          return (
            <button
              key={option.code}
              onClick={() => choose(option.code)}
              disabled={ocupado}
              aria-busy={carregando}
              className={`w-full px-4 py-2 text-left text-xs font-display hover:bg-secondary/60 transition-colors flex items-center gap-2 ${
                lang === option.code || carregando ? "text-primary" : "text-foreground"
              } ${ocupado && !carregando ? "opacity-40" : ""} disabled:cursor-default`}
            >
              <span>{option.nativeLabel}</span>
              {carregando && (
                <Loader2
                  className="w-3 h-3 animate-spin ml-auto"
                  aria-hidden="true"
                  data-testid={`language-spinner-${option.code}`}
                />
              )}
            </button>
          );
        })}
        {linhaDeErro}
      </>
    );
  }

  return (
    <>
      {LANGUAGES.map((option) => {
        const active = option.code === lang;
        const carregando = pendingLang === option.code;
        return (
          <button
            key={option.code}
            role="menuitemradio"
            aria-checked={active}
            aria-busy={carregando}
            disabled={ocupado}
            onClick={() => choose(option.code)}
            className="w-full flex items-center gap-3 px-4 py-3 text-left text-[14px] font-display transition-colors disabled:cursor-default"
            style={{
              color: "rgba(255,255,255,0.85)",
              background: active || carregando ? "hsl(187 72% 50% / 0.10)" : "transparent",
              opacity: ocupado && !carregando ? 0.4 : 1,
            }}
          >
            <span className="text-[18px] leading-none">{option.flag}</span>
            <span>{option.nativeLabel}</span>
            {carregando ? (
              // Ocupa o mesmo lugar do ponto de "ativo": o menu não muda de
              // altura nem dança quando o spinner entra e sai.
              <Loader2
                className="ml-auto w-3 h-3 animate-spin"
                style={{ color: "hsl(187 72% 50%)" }}
                aria-hidden="true"
                data-testid={`language-spinner-${option.code}`}
              />
            ) : active ? (
              <span
                className="ml-auto w-2 h-2 rounded-full"
                style={{ background: "hsl(187 72% 50%)" }}
              />
            ) : null}
          </button>
        );
      })}
      {linhaDeErro}
    </>
  );
};

export default LanguageSelector;
